// components/common/MapDisplacementLine.tsx
import { useMap } from '@vis.gl/react-google-maps';
import React from 'react';
import { DisplacementResult, displaceGPS } from 'utils/geoCalculations';

export interface MapDisplacementLineProps {
  displacementResult: DisplacementResult | null | undefined;
  show: boolean;
  cameraBearing?: number | null;
}

const MapDisplacementLine: React.FC<MapDisplacementLineProps> = ({
  displacementResult,
  show,
  cameraBearing
}) => {
  const map = useMap();
  const [polyline, setPolyline] = React.useState<google.maps.Polyline | null>(
    null
  );
  const [directionLine, setDirectionLine] =
    React.useState<google.maps.Polyline | null>(null);
  const [originalMarker, setOriginalMarker] =
    React.useState<google.maps.Marker | null>(null);
  const [adjustedMarker, setAdjustedMarker] =
    React.useState<google.maps.Marker | null>(null);
  const [cameraDirectionMarker, setCameraDirectionMarker] =
    React.useState<google.maps.Marker | null>(null);

  React.useEffect(() => {
    console.log('MapDisplacementLine: Update', {
      hasMap: !!map,
      show,
      hasResult: !!displacementResult,
      cameraBearing,
      result: displacementResult
    });

    if (!map || !show || !displacementResult) {
      // Limpar elementos do mapa
      if (polyline) {
        polyline.setMap(null);
        setPolyline(null);
      }
      if (directionLine) {
        directionLine.setMap(null);
        setDirectionLine(null);
      }
      if (originalMarker) {
        originalMarker.setMap(null);
        setOriginalMarker(null);
      }
      if (adjustedMarker) {
        adjustedMarker.setMap(null);
        setAdjustedMarker(null);
      }
      if (cameraDirectionMarker) {
        cameraDirectionMarker.setMap(null);
        setCameraDirectionMarker(null);
      }
      return;
    }

    const { originalGPS, adjustedGPS, horizontalOffset, depthOffset } =
      displacementResult;

    console.log('MapDisplacementLine: Drawing', {
      original: originalGPS,
      adjusted: adjustedGPS,
      h: horizontalOffset,
      d: depthOffset,
      bearing: cameraBearing
    });

    // 1. LINHA DE DESLOCAMENTO (Original -> Ajustado)
    if (!polyline) {
      const newPolyline = new google.maps.Polyline({
        path: [
          { lat: originalGPS.lat, lng: originalGPS.lng },
          { lat: adjustedGPS.lat, lng: adjustedGPS.lng }
        ],
        geodesic: true,
        strokeColor: '#ffc107',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map: map
      });
      setPolyline(newPolyline);
      console.log('MapDisplacementLine: Polyline created');
    } else {
      polyline.setPath([
        { lat: originalGPS.lat, lng: originalGPS.lng },
        { lat: adjustedGPS.lat, lng: adjustedGPS.lng }
      ]);
    }

    // 2. LINHA DE DIREÇÃO DA CÂMERA (se bearing disponível)
    if (cameraBearing !== null && cameraBearing !== undefined) {
      // Calcular um ponto à frente na direção da câmera (20 metros)
      const directionPoint = displaceGPS(
        originalGPS,
        cameraBearing,
        20 // 20 metros de linha de direção
      );

      if (!directionLine) {
        const newDirectionLine = new google.maps.Polyline({
          path: [
            { lat: originalGPS.lat, lng: originalGPS.lng },
            { lat: directionPoint.lat, lng: directionPoint.lng }
          ],
          geodesic: true,
          strokeColor: '#007bff',
          strokeOpacity: 0.6,
          strokeWeight: 2,
          icons: [
            {
              icon: {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: '#007bff',
                fillColor: '#007bff',
                fillOpacity: 0.8
              },
              offset: '100%'
            }
          ],
          map: map
        });
        setDirectionLine(newDirectionLine);
        console.log('MapDisplacementLine: Direction line created');
      } else {
        directionLine.setPath([
          { lat: originalGPS.lat, lng: originalGPS.lng },
          { lat: directionPoint.lat, lng: directionPoint.lng }
        ]);
      }

      // Marker na ponta da linha de direção
      if (!cameraDirectionMarker) {
        const newMarker = new google.maps.Marker({
          position: { lat: directionPoint.lat, lng: directionPoint.lng },
          map: map,
          icon: {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 4,
            fillColor: '#007bff',
            fillOpacity: 0.8,
            strokeColor: '#ffffff',
            strokeWeight: 1,
            rotation: cameraBearing
          },
          title: `Direção da Câmera: ${cameraBearing.toFixed(1)}°`
        });
        setCameraDirectionMarker(newMarker);
      } else {
        cameraDirectionMarker.setPosition({
          lat: directionPoint.lat,
          lng: directionPoint.lng
        });
        cameraDirectionMarker.setIcon({
          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 4,
          fillColor: '#007bff',
          fillOpacity: 0.8,
          strokeColor: '#ffffff',
          strokeWeight: 1,
          rotation: cameraBearing
        });
      }
    } else if (directionLine) {
      // Remover linha de direção se bearing não disponível
      directionLine.setMap(null);
      setDirectionLine(null);
      if (cameraDirectionMarker) {
        cameraDirectionMarker.setMap(null);
        setCameraDirectionMarker(null);
      }
    }

    // 3. MARKER ORIGINAL (GPS da foto - vermelho)
    if (!originalMarker) {
      const newMarker = new google.maps.Marker({
        position: { lat: originalGPS.lat, lng: originalGPS.lng },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#dc3545',
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 2
        },
        title: `GPS Original (Frame)\n${originalGPS.lat.toFixed(
          6
        )}, ${originalGPS.lng.toFixed(6)}`
      });
      setOriginalMarker(newMarker);
      console.log('MapDisplacementLine: Original marker created');
    } else {
      originalMarker.setPosition({
        lat: originalGPS.lat,
        lng: originalGPS.lng
      });
    }

    // 4. MARKER AJUSTADO (posição calculada - verde/azul/amarelo)
    const markerColor =
      Math.abs(horizontalOffset) > 0.5
        ? horizontalOffset > 0
          ? '#28a745' // Verde - direita
          : '#007bff' // Azul - esquerda
        : '#ffc107'; // Amarelo - centro

    if (!adjustedMarker) {
      const newMarker = new google.maps.Marker({
        position: { lat: adjustedGPS.lat, lng: adjustedGPS.lng },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: markerColor,
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 2
        },
        title: `Posição Ajustada\nH: ${horizontalOffset.toFixed(
          2
        )}m | D: ${depthOffset.toFixed(2)}m\n${adjustedGPS.lat.toFixed(
          6
        )}, ${adjustedGPS.lng.toFixed(6)}`
      });
      setAdjustedMarker(newMarker);
      console.log('MapDisplacementLine: Adjusted marker created');
    } else {
      adjustedMarker.setPosition({
        lat: adjustedGPS.lat,
        lng: adjustedGPS.lng
      });
      adjustedMarker.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: markerColor,
        fillOpacity: 0.9,
        strokeColor: '#ffffff',
        strokeWeight: 2
      });
      adjustedMarker.setTitle(
        `Posição Ajustada\nH: ${horizontalOffset.toFixed(
          2
        )}m | D: ${depthOffset.toFixed(2)}m\n${adjustedGPS.lat.toFixed(
          6
        )}, ${adjustedGPS.lng.toFixed(6)}`
      );
    }
  }, [
    map,
    show,
    displacementResult,
    cameraBearing,
    polyline,
    directionLine,
    originalMarker,
    adjustedMarker,
    cameraDirectionMarker
  ]);

  return null;
};

export default MapDisplacementLine;
